import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Stack,
    useTheme,
    useMediaQuery, // Added for responsiveness
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import {
    Share as ShareIcon
} from '@mui/icons-material';
import TimelineDetailView from './TimelineDetailView'; // Import the new view component

// For demonstration, let's define a robust formatDate if you need one:
export const formatDate = (dateString, options = { year: 'numeric', month: 'long', day: 'numeric' }) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A'; // Invalid date
        return date.toLocaleDateString('en-CA', options);
    } catch (e) {
        return 'N/A';
    }
};

const displayData = (data, prefix = '', suffix = '') => {
    if (data === null || typeof data === 'undefined' || data === '') {
        return 'N/A';
    }
    // displayBoolean now handles boolean, this is a fallback or for non-chip booleans.
    if (typeof data === 'boolean') {
        return data ? 'Yes' : 'No';
    }
    return `${prefix}${data}${suffix}`;
};


const CaseDetailModal = ({ open, onClose, caseData, onFollowToggle, isFollowing, isProcessingFollow, isOwnTimeline }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Use 'sm' for mobile breakpoint
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const handleShare = () => {
        const shareUrl = `${window.location.origin}/timeline/share/${caseData._id}`;
        navigator.clipboard.writeText(shareUrl).then(() => {
            setSnackbarOpen(true);
        });
    };

    if (!caseData) {
        return null;
    }

    const { _id, applicationStream, voOffice } = caseData;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile} // Make dialog fullscreen on mobile
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3, // No border radius in fullscreen mode
                    height: isMobile ? '100%' : 'auto' // Ensure full height on mobile
                }
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    borderTopLeftRadius: isMobile ? 0 : '12px', // Match PaperProps
                    borderTopRightRadius: isMobile ? 0 : '12px', // Match PaperProps
                    pt: isMobile ? 2 : 1.5, // More padding top on mobile if fullscreen
                    pb: 1.5
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant={isMobile ? "h6" : "h5"} component="div" sx={{ fontWeight: 'bold' }}>
                            PR Application Details
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85 }}>
                            Case ID: {displayData(_id)}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end" useFlexGap> {/* Allow chips to wrap */}
                        <IconButton onClick={handleShare} size="small" sx={{ color: 'white' }}>
                            <ShareIcon />
                        </IconButton>
                        {applicationStream && <Chip label={applicationStream} size="small" sx={{backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, fontWeight:'medium', mt: isMobile ? 0.5: 0}} />}
                        {voOffice && <Chip label={`VO: ${voOffice}`} size="small" sx={{backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, fontWeight:'medium', mt: isMobile ? 0.5: 0}} />}
                    </Stack>
                </Box>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
                    p: { xs: 1.5, sm: 2, md: 2.5 } // Responsive padding
                }}
            >
                <TimelineDetailView timelineData={caseData} />
            </DialogContent>
            <DialogActions
                sx={{
                    padding: { xs: '12px 12px', sm: '16px 24px' },
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
                    borderBottomLeftRadius: isMobile ? 0 : '12px', // Match PaperProps
                    borderBottomRightRadius: isMobile ? 0 : '12px', // Match PaperProps
                    borderTop: `1px solid ${theme.palette.divider}`,
                    flexDirection: { xs: 'column-reverse', sm: 'row' }, // Stack buttons on mobile
                    gap: { xs: 1, sm: 1 } // Add gap between buttons
                }}
            >
                <Button
                    onClick={onClose}
                    color="inherit"
                    variant={isMobile ? "outlined" : "text"} // More prominent close button on mobile if stacked
                    sx={{ width: { xs: '100%', sm: 'auto' } }} // Full width on mobile when stacked
                >
                    Close
                </Button>
                <Button
                    onClick={() => onFollowToggle(_id, isFollowing)}
                    color={isFollowing ? "secondary" : "primary"}
                    variant="contained"
                    disabled={isProcessingFollow || isOwnTimeline}
                    sx={{
                        borderRadius: '8px',
                        boxShadow: theme.shadows[2],
                        width: { xs: '100%', sm: 'auto' } // Full width on mobile when stacked
                    }}
                >
                    {isOwnTimeline ? "This is Your Timeline" : isProcessingFollow ? 'Processing...' : (isFollowing ? 'Unfollow Case' : 'Follow Case')}
                </Button>
            </DialogActions>
        </Dialog>
        <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
                Link copied to clipboard!
            </Alert>
        </Snackbar>
        </>
    );
};

export default CaseDetailModal;